import React, {useEffect} from 'react';
import classnames from 'classnames';
import {withRouter} from 'react-router';
import PropTypes from 'prop-types';
import {isEmpty} from 'lodash';
import {withApplicationMetadata} from 'client/Routing/withApplicationMetadata';
import sessionStore from 'client/Session/SessionStore';
import constants from 'client/constants';
import appStore from 'client/AppStore';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {getQueryParamsAsObject, removeQueryParams} from 'client/history';

import {
  canCreateTemplateForSubject,
  getUserSubjectAccessQuery,
  isUnlicensedTeacherOrAdmin,
  userLicensedSubjectsQuery
} from 'lib/UserAccessUtil';
import LoadingIndicator from 'generic/LoadingIndicator.react';
import {Heading, LoadingSpinner, WindowSizeConsumer} from '@albert-io/atomic';
import UpgradeSubjectModal from 'components/PracticeView/UpgradeSubjectModal/UpgradeSubjectModal.react';
import {buildGuideTree} from 'client/components/ContentDiscovery/ManageRestrictionsModal';
import {
  getTeacherRestrictedGuideLevelIds,
  getGuideLevelIdsWithLockedDescendant
} from 'client/components/TeacherRestrictedContent';
import {StudyGuideItemList} from 'client/generic/StudyGuideItem/StudyGuideItem.react';
import AdvancedSearch from 'client/components/AdvancedSearch/AdvancedSearch.react';
import {getGuideLevelsBySearchParams} from 'client/components/AdvancedSearch/AdvancedSearch.queries';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';
import {GuideLevelsCollectionModel} from 'resources/GuideLevel/GuideLevelsCollection.model';
import {GuideModelV1} from 'resources/augmented/Guide/GuideModel.v1';
import {UserModelV2} from 'resources/GeneratedModels/User/UserModel.v2';

import {showPublicViewForSubject} from 'components/PracticeView/practiceView.utils';

import {List} from 'immutable';

import {getSearchParams, isSearchMode} from '../components/AdvancedSearch/AdvancedSearch.utils';
import {hasStandardsQuery, hasTagsQuery} from '../components/AdvancedSearch/AdvancedSearch.queries';

import {GuideLevelCard} from './GuideLevelCard';
import MarketingBanner from './MarketingBanner/MarketingBanner.react';
import studyGuideUtils from './utils';
import {ThemeMenu} from './ThemeMenu/ThemeMenu';

import './study-guide.scss';

const NoResultsFound = () => (
  <div className='mt-x2 rounded-x1 border-border-primary background-primary p-x5 w-full border border-solid'>
    <Heading as='center' size='m'>
      No matches found
    </Heading>
  </div>
);

export class GuideLevelsList extends React.Component {
  static propTypes = {
    guide: PropTypes.instanceOf(GuideModelV1),
    guideLevelsBySearchParams: PropTypes.instanceOf(GuideLevelsCollectionModel),
    location: PropTypes.shape({
      hash: PropTypes.string,
      query: PropTypes.shape({
        s: PropTypes.string,
        standards: PropTypes.string,
        tags: PropTypes.string
      }),
      search: PropTypes.string
    }),
    subject: PropTypes.object,
    user: PropTypes.instanceOf(UserModelV2),
    licensedSubjects: ImmutablePropTypes.list,
    usePublicView: PropTypes.bool,
    showAdvancedSearch: PropTypes.bool,
    onSearchCallback: PropTypes.func,
    isReady: PropTypes.bool,
    partialCreditEnabled: PropTypes.bool
  };

  constructor() {
    super();
    this.state = {
      shouldShowUpgradeSubjectModal: false,
      hasPopover: false
    };
    this.autoOpenedForSubjectId = null;
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
    this.bootstrapComponentState();
  }

  componentDidUpdate(prevProps) {
    // We may only need to compare if the guide changes but we are comparing all props to be safe
    if (
      prevProps.location?.query?.s !== this.props.location?.query?.s ||
      prevProps.guide !== this.props.guide ||
      prevProps.subject !== this.props.subject ||
      prevProps.licensedSubjects !== this.props.licensedSubjects
    ) {
      this.bootstrapComponentState();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  bootstrapComponentState() {
    const {guide, subject, licensedSubjects} = this.props;
    if (
      sessionStore.hasValidSession() &&
      !licensedSubjects.isEmpty() &&
      !subject.doesUserHaveAccess() &&
      this.autoOpenedForSubjectId !== subject.getId()
    ) {
      this.autoOpenedForSubjectId = subject.getId();
      this.openUpgradeSubjectModal();
    }

    const guideLevels = guide
      .getSortedGuideLevels()
      .filter((guideLevel) => guideLevel.hasQuestions())
      .filter(this.shouldDisplayGuideLevel);
    const themes = guideLevels.filter((guideLevel) => guideLevel.getNlevel() === 1);
    const themeRefs = themes.reduce((acc, theme) => {
      const ref = React.createRef();
      return {
        ...acc,
        [theme.getId()]: ref
      };
    }, {});

    // add scroll to slug behavior (see https://github.com/albert-io/project-management/issues/4140)
    const themeFromUrl = themes.find(
      (theme) => theme.getUrlSlug() === this.props.location.hash.substring(1)
    );

    this.setState({
      subject,
      licensedSubjects,
      themes,
      themeRefs,
      initialTheme: themeFromUrl?.getId()
    });
  }

  getMatchingQuestionCount = (guideLevel) => {
    const matchingSearchResult = this.props.guideLevelsBySearchParams
      .getCollection()
      .find((result) => result.getId() === guideLevel.getId());

    return matchingSearchResult.getMeta().getTotalMatchingQuestions() || 0;
  };

  shouldDisplayGuideLevel = (guideLevel) => {
    if (!isSearchMode()) {
      return true;
    }
    const matchingGuideLevelIds = this.props.guideLevelsBySearchParams.cache('f', () => {
      return this.props.guideLevelsBySearchParams
        .getCollection()
        .toSet()
        .map((level) => level.getId());
    });
    return matchingGuideLevelIds.includes(guideLevel.getId());
  };

  openUpgradeSubjectModal = () => {
    this.setState({shouldShowUpgradeSubjectModal: true});
  };

  closeUpgradeSubjectModal = () => {
    this.setState({shouldShowUpgradeSubjectModal: false});
  };

  getStudyGuideLink = (linkToQuestions, queryString) => {
    const currentRoute =
      appStore.routerProps.location.pathname + appStore.routerProps.location.search;
    return {
      pathname: linkToQuestions,
      search: queryString,
      state: {
        from: currentRoute
      }
    };
  };

  onKeyPress = (e, onClick) => {
    if (e.key === 'Enter') {
      onClick(e);
    }
  };

  handleScroll = () => {
    const el = document.querySelector('.study-guide__sticky-search--mobile');
    if (el) {
      // handles logic for adding the box shadow to the bottom of the stickied nav
      const stickyTop = parseInt(window.getComputedStyle(el).top, 10);
      const currentTop = el.getBoundingClientRect().top;
      const stickyClass = 'study-guide__sticky-search--is-sticky';
      if (currentTop === stickyTop) {
        el.classList.add(stickyClass);
      } else {
        el.classList.remove(stickyClass);
      }
    }
  };

  render() {
    const {subject, licensedSubjects, themes, themeRefs, initialTheme, hasPopover} = this.state;
    const {
      guide,
      showAdvancedSearch,
      onSearchCallback,
      guideLevelsBySearchParams,
      isReady,
      partialCreditEnabled
    } = this.props;
    if (!subject || !guide) {
      return null;
    }
    const allGuideLevels = guide.getSortedGuideLevels();
    const guideLevels = allGuideLevels
      .filter((guideLevel) => guideLevel.hasQuestions())
      .filter(this.shouldDisplayGuideLevel);
    // Tree built from the full, unfiltered guide-level list so the indicators + modal can derive
    // effective-lock state (a parent lock cascades down; all children locked rolls up).
    const guideLevelTree = buildGuideTree(
      allGuideLevels
        .toArray()
        .map((level) => ({id: level.getId(), parentId: level.getParentId() || null}))
    );
    // Teacher indicator: roll child locks up to a parent (a quiz whose question types are all locked
    // shows the indicator). Built from the question-bearing tree so it's stable across search/display.
    const teacherRestrictedGuideLevelIds = getTeacherRestrictedGuideLevelIds(
      allGuideLevels.filter((guideLevel) => guideLevel.hasQuestions()).toArray()
    );
    // DQA #17841: flag a parent theme that contains any locked guide level (any-descendant roll-up).
    const guideLevelIdsWithLockedDescendant = getGuideLevelIdsWithLockedDescendant(
      allGuideLevels.filter((guideLevel) => guideLevel.hasQuestions()).toArray()
    );
    const hasTeacherAccess = sessionStore.isTeacher();
    const hasAccessToCreateTemplate = canCreateTemplateForSubject(subject.getId());
    return (
      <WindowSizeConsumer>
        {({breakpoints, viewportWidth}) => {
          const isMobileView = viewportWidth <= breakpoints.s;
          const hideThemeDropdownCTAText = viewportWidth <= breakpoints.xs;
          const isEmptySearches =
            isSearchMode() && guideLevelsBySearchParams.getCollection().isEmpty();
          return (
            <div className='study-guide-wrapper'>
              <div
                className={classnames('study-guide__sticky-search', {
                  // when popover backdrop is open, we need to increase the z-index of the sticky search by 1
                  'study-guide__sticky-search--mobile': isMobileView,
                  'study-guide__sticky-search--zIndex':
                    hasPopover && viewportWidth <= breakpoints.xs
                })}
              >
                <AdvancedSearch
                  showFilters={showAdvancedSearch}
                  key={appStore.routerProps.location.pathname}
                  onSearchCallback={onSearchCallback}
                  subjectId={subject.getId()}
                  isMobileView={isMobileView}
                  isEmptySearches={isEmptySearches}
                  themes={themes}
                  themeRefs={themeRefs}
                  hideThemeDropdownCTAText={hideThemeDropdownCTAText}
                  setHasPopover={(val) => this.setState({hasPopover: val})}
                />
              </div>
              {!isReady ? (
                <LoadingIndicator />
              ) : (
                <div className='study-guide-content'>
                  <div className='study-guide-content__split-pane'>
                    <ThemeMenu
                      themes={themes}
                      themeRefs={themeRefs}
                      initialTheme={initialTheme}
                      hideSidebar={isMobileView}
                    />
                    {isEmptySearches ? (
                      <NoResultsFound />
                    ) : (
                      <div className='study-guide-cards'>
                        <StudyGuideItemList>
                          {guideLevels.map((guideLevel, i, displayedGuideLevels) => (
                            <GuideLevelCard
                              key={guideLevel.id}
                              guideLevel={guideLevel}
                              nextGuideLevel={displayedGuideLevels.get(i + 1)}
                              subject={subject}
                              licensedSubjects={licensedSubjects}
                              locationQuery={this.props.location.query}
                              usePublicView={this.props.usePublicView}
                              isAssessment={
                                guide.getGuideType() === constants.GUIDE_TYPES.ASSESSMENT
                              }
                              hasTeacherAccess={hasTeacherAccess}
                              hasAccessToCreateTemplate={hasAccessToCreateTemplate.value}
                              partialCreditEnabled={partialCreditEnabled}
                              guideLevelTree={guideLevelTree}
                              teacherRestrictedGuideLevelIds={teacherRestrictedGuideLevelIds}
                              guideLevelIdsWithLockedDescendant={guideLevelIdsWithLockedDescendant}
                              themeRefs={themeRefs}
                              getMatchingQuestionCount={this.getMatchingQuestionCount}
                              getStudyGuideLink={this.getStudyGuideLink}
                              openUpgradeSubjectModal={this.openUpgradeSubjectModal}
                              onKeyPress={this.onKeyPress}
                            />
                          ))}
                        </StudyGuideItemList>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {this.state.shouldShowUpgradeSubjectModal && (
                <UpgradeSubjectModal
                  subject={this.props.subject}
                  handleClose={this.closeUpgradeSubjectModal}
                />
              )}
            </div>
          );
        }}
      </WindowSizeConsumer>
    );
  }
}

function GuideLevelsListWithQueries(props) {
  const {subject, location} = props;
  const params = getSearchParams();

  // Caching tags, standards, and subject access queries since we know they are going to be used by children components
  // This allows us to fetch everything in a single load time
  const guideType = location.pathname.includes('assessments') ? 'assessment' : 'practice';

  const queries = [
    studyGuideUtils.getGuideBySubjectIdQuery(subject.getId(), guideType),
    userLicensedSubjectsQuery(),
    hasTagsQuery(subject.getId()),
    hasStandardsQuery(subject.getId()),
    getUserSubjectAccessQuery(subject.getId())
  ];

  if (sessionStore.hasValidSession()) {
    queries.push(
      studyGuideUtils.getClassroomsByUserIdQuery(sessionStore.getUserId(), sessionStore.isStudent())
    );
  }

  if (isSearchMode()) {
    queries.push(
      getGuideLevelsBySearchParams({
        ...params,
        subjectId: subject.getId(),
        guideType
      })
    );
  }

  const {isReady, resources} = useMandarkQueries(queries);

  const [guide, licensedSubjects, __cachedTags, __cachedStandards, __cachedSubjectAccess, ...rest] =
    resources;

  let classrooms = List();
  let guideLevelsBySearchParams = List();

  if (sessionStore.hasValidSession() && isSearchMode()) {
    [classrooms, guideLevelsBySearchParams] = rest;
  } else if (sessionStore.hasValidSession()) {
    [classrooms] = rest;
  } else if (isSearchMode()) {
    [guideLevelsBySearchParams] = rest;
  }

  const partialCreditEnabled = studyGuideUtils.isPartialCreditEnabledForSubject(
    subject,
    classrooms,
    sessionStore.isStudent()
  );

  return (
    <GuideLevelsList
      {...props}
      guide={guide}
      guideLevelsBySearchParams={guideLevelsBySearchParams}
      isReady={isReady}
      licensedSubjects={licensedSubjects}
      partialCreditEnabled={partialCreditEnabled}
    />
  );
}

const BootstrappedGuideLevelsList = withRouter(
  withApplicationMetadata(GuideLevelsListWithQueries, (props) => {
    const guideType = props.location.pathname.includes('assessments') ? 'Assessment' : 'Practice';

    return {
      title: `${props.subject.getName()} | ${guideType} | Albert`,
      metaDescription: props.subject.getDescription()
    };
  })
);

const StudyGuide = ({subject, user, onSearchCallback, location}) => {
  const {isReady, value: isUnlicensedNonStudent} = isUnlicensedTeacherOrAdmin();

  const guideType = location.pathname.includes('assessments') ? 'assessment' : 'practice';
  const isAssessment = guideType === 'assessment';
  const showAdvancedSearch = !(
    isAssessment &&
    (!sessionStore.hasValidSession() || isUnlicensedNonStudent || sessionStore.isStudent())
  );

  // Dep-less on purpose: the gate flips when the access check becomes ready, and that pass
  // leaves `location.search` untouched, so search-keyed deps would never strip the params.
  useEffect(() => {
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- clearing a filter the hidden search UI can't reach is a real side effect
    if (!isReady || showAdvancedSearch) {
      return;
    }
    const params = getQueryParamsAsObject();
    if (!isEmpty(params)) {
      removeQueryParams(...Object.keys(params));
    }
  });

  if (!isReady) {
    return <LoadingSpinner />;
  }

  const usePublicView = showPublicViewForSubject(subject);
  return (
    <>
      <MarketingBanner user={user} subject={subject} className='px-x2' />
      <BootstrappedGuideLevelsList
        subject={subject}
        user={user}
        usePublicView={usePublicView}
        showAdvancedSearch={showAdvancedSearch}
        onSearchCallback={onSearchCallback}
      />
    </>
  );
};

StudyGuide.propTypes = {
  subject: PropTypes.object,
  user: PropTypes.instanceOf(UserModelV2),
  onSearchCallback: PropTypes.func,
  location: PropTypes.object
};

export default StudyGuide;
